import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Wifi, WifiOff } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

export const LiveChat = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  const connectWebSocket = useCallback((sessionId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const ws = new WebSocket(`${WS_URL}/api/chat/ws/${sessionId}`);
    
    ws.onopen = () => {
      setWsConnected(true);
    };
    
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        setMessages(prev => {
          // Avoid duplicates
          if (prev.find(m => m.message_id === msg.message_id)) return prev;
          return [...prev, msg];
        });
      } catch (e) {}
    };
    
    ws.onclose = () => {
      setWsConnected(false);
      // Auto-reconnect after 3s if session still open
      if (session) {
        setTimeout(() => {
          if (session) connectWebSocket(sessionId);
        }, 3000);
      }
    };
    
    ws.onerror = () => {
      setWsConnected(false);
    };
    
    wsRef.current = ws;
  }, [session]);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const startSession = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/chat/start`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setSession(res.data);
      fetchMessages(res.data.session_id);
      connectWebSocket(res.data.session_id);
    } catch (e) {}
  };

  const fetchMessages = async (sessionId) => {
    const sid = sessionId || session?.session_id;
    if (!sid) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/chat/messages/${sid}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setMessages(res.data);
    } catch (e) {}
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/chat/message`, {
        message: input, session_id: session.session_id
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      // Add message locally (WebSocket might also deliver it, deduped by message_id)
      setMessages(prev => {
        if (prev.find(m => m.message_id === res.data.message_id)) return prev;
        return [...prev, res.data];
      });
      setInput("");
    } catch (e) {} finally { setSending(false); }
  };

  const handleOpen = () => {
    setOpen(true);
    if (user && !session) startSession();
  };

  const handleClose = () => {
    setOpen(false);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  if (!user) return null;

  return (
    <>
      {!open && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-neon-cyan text-black rounded-full shadow-lg shadow-neon-cyan/30 flex items-center justify-center hover:scale-110 transition-transform"
          data-testid="chat-toggle-button"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 h-[28rem] flex flex-col bg-zinc-900 border border-white/10 shadow-2xl" data-testid="chat-window">
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-zinc-900">
            <div>
              <h3 className="font-heading text-sm uppercase tracking-wide">Gas Station Support</h3>
              <p className="text-xs flex items-center gap-1">
                {wsConnected ? (
                  <><Wifi className="w-3 h-3 text-neon-green" /><span className="text-neon-green">Live</span></>
                ) : (
                  <><WifiOff className="w-3 h-3 text-amber-400" /><span className="text-amber-400">Connecting...</span></>
                )}
              </p>
            </div>
            <button onClick={handleClose} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center text-zinc-500 text-sm py-8">
                <p>Hey! How can we help?</p>
                <p className="text-xs mt-1">Send a message to start chatting.</p>
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.message_id}
                className={`flex ${msg.sender_type === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[75%] p-3 text-sm ${
                  msg.sender_type === "user"
                    ? "bg-neon-cyan/20 text-white"
                    : "bg-white/10 text-zinc-300"
                }`}>
                  {msg.sender_type === "admin" && (
                    <p className="text-xs text-neon-green font-medium mb-1">Support</p>
                  )}
                  <p>{msg.message}</p>
                  <p className="text-xs text-zinc-600 mt-1">
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSend} className="p-3 border-t border-white/10 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message..."
              className="bg-black/50 border-white/10 text-sm flex-1"
              data-testid="chat-input"
            />
            <Button type="submit" disabled={sending || !input.trim()} size="icon" className="bg-neon-cyan text-black flex-shrink-0" data-testid="chat-send-button">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      )}
    </>
  );
};

export default LiveChat;
